import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HotelAdminStats, 
  HotelStaffMember, 
  RecentAdminActivity 
} from '../types/admin';
import { hotelAdminService } from '../services/hotelAdminService';
import { AdminStatsCard } from '../components/admin/AdminStatsCard';
import { StaffList } from '../components/admin/StaffList';
import { RecentActivityFeed } from '../components/admin/RecentActivityFeed';
import { useHotelPermissions } from '../hooks/useHotelPermissions';

/** LED indicator for FPT payment status */
function PaymentStatusLED({ status }: { status?: string }) {
  const config: Record<string, { color: string; bg: string; label: string }> = {
    paid:    { color: 'bg-green-500', bg: 'bg-green-50', label: 'À jour' },
    partial: { color: 'bg-orange-400', bg: 'bg-orange-50', label: 'Paiement partiel' },
    overdue: { color: 'bg-red-500', bg: 'bg-red-50', label: 'Impayé / En retard' },
    unknown: { color: 'bg-gray-300', bg: 'bg-gray-50', label: 'Non disponible' },
  };
  const { color, bg, label } = config[status || 'unknown'] || config.unknown;
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${bg}`}>
      <span className={`w-3 h-3 rounded-full ${color} inline-block`} aria-hidden="true" />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </div>
  );
}

export function HotelAdminDashboardPage() {
  const [stats, setStats] = useState<HotelAdminStats | null>(null);
  const [staff, setStaff] = useState<HotelStaffMember[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentAdminActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fptStatus, setFptStatus] = useState<string>('unknown');
  const { isHotelAdmin, isAdminOrManager, userRole } = useHotelPermissions();

  useEffect(() => {
    let cancelled = false;
    
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [statsResult, staffResult, activityResult] = await Promise.allSettled([
          hotelAdminService.getHotelStats(),
          hotelAdminService.getStaffMembers(),
          hotelAdminService.getRecentActivity()
        ]);

        if (cancelled) {
          return;
        }

        const statsData = statsResult.status === 'fulfilled' ? statsResult.value : null;
        const staffData = staffResult.status === 'fulfilled' ? (staffResult.value || []) : [];
        const activityData = activityResult.status === 'fulfilled' ? (activityResult.value || []) : [];
        
        setStats(statsData);
        setStaff(staffData);
        setRecentActivity(activityData);
        
        // Derive FPT status from stats if available
        if (statsData) {
          const s = statsData as any;
          if (s.fpt_status) {
            setFptStatus(s.fpt_status);
          } else if (s.fptStatus) {
            setFptStatus(s.fptStatus);
          }
        }
        
        // Only show error if ALL calls failed
        if (statsResult.status === 'rejected' && staffResult.status === 'rejected' && activityResult.status === 'rejected') {
          setError('Impossible de charger les données du tableau de bord. Vérifiez la connexion au serveur.');
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        if (!cancelled) {
          setError('Erreur lors du chargement des données');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();
    
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsResult, staffResult, activityResult] = await Promise.allSettled([
        hotelAdminService.getHotelStats(),
        hotelAdminService.getStaffMembers(),
        hotelAdminService.getRecentActivity()
      ]);
      
      const statsData = statsResult.status === 'fulfilled' ? statsResult.value : null;
      const staffData = staffResult.status === 'fulfilled' ? (staffResult.value || []) : [];
      const activityData = activityResult.status === 'fulfilled' ? (activityResult.value || []) : [];
      
      setStats(statsData);
      setStaff(staffData);
      setRecentActivity(activityData);
      
      if (statsData) {
        const s = statsData as any;
        if (s.fpt_status) setFptStatus(s.fpt_status);
        else if (s.fptStatus) setFptStatus(s.fptStatus);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleEditStaff = (staffMember: HotelStaffMember) => {
    // TODO: implement staff editing UI
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-4 text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button 
            onClick={handleRefresh}
            className="ml-4 text-red-800 underline hover:no-underline"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with role indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administration de l'Hôtel</h1>
          <p className="text-gray-600 mt-1">
            Gérez votre personnel, paramètres et visualisez les statistiques
          </p>
          <RoleBadge role={userRole} />
        </div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Stats Cards - hotel-specific metrics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <AdminStatsCard
            title="Invités Actuels"
            value={stats.totalGuests}
            icon="👥"
            subtitle={`${stats.recentRegistrations} enregistrements récents`}
            color="green"
          />
          <AdminStatsCard
            title="Taux d'Occupation"
            value={`${stats.occupancyRate.toFixed(1)}%`}
            icon="📊"
            subtitle={`${stats.occupiedRooms}/${stats.totalRooms} chambres`}
            color="blue"
          />
          <AdminStatsCard
            title="Personnel"
            value={stats.staffCount}
            icon="🧑‍💼"
            subtitle={`${stats.checkInsToday} arrivées aujourd'hui`}
            color="purple"
          />
          <AdminStatsCard
            title="Revenus du Jour"
            value={formatCurrency(stats.totalRevenue)}
            icon="💰"
            subtitle={`Séjour moyen: ${stats.averageStayDuration.toFixed(1)}j`}
            color="yellow"
          />
        </div>
      )}

      {/* FPT Payment Status Indicator */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Statut FPT (Frais de Promotion Touristique)</h3>
            <p className="text-sm text-gray-500 mt-1">Situation de paiement de la taxe touristique</p>
          </div>
          <PaymentStatusLED status={fptStatus} />
        </div>
      </div>

      {/* Admin Module Navigation */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Modules d'Administration</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {isHotelAdmin && (
            <Link 
              to="/admin/staff" 
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center group"
            >
              <div className="text-3xl mb-2">👥</div>
              <div className="text-sm font-medium group-hover:text-blue-700">Gestion Personnel</div>
              <p className="text-xs text-gray-400 mt-1">Utilisateurs & rôles</p>
            </Link>
          )}
          {isAdminOrManager && (
            <Link 
              to="/admin/settings" 
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center group"
            >
              <div className="text-3xl mb-2">⚙️</div>
              <div className="text-sm font-medium group-hover:text-blue-700">Paramètres</div>
              <p className="text-xs text-gray-400 mt-1">Configuration hôtel</p>
            </Link>
          )}
          {isAdminOrManager && (
            <Link 
              to="/reports" 
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center group"
            >
              <div className="text-3xl mb-2">📊</div>
              <div className="text-sm font-medium group-hover:text-blue-700">Rapports</div>
              <p className="text-xs text-gray-400 mt-1">Statistiques & exports</p>
            </Link>
          )}
          <Link 
            to="/guests" 
            className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center group"
          >
            <div className="text-3xl mb-2">🏨</div>
            <div className="text-sm font-medium group-hover:text-blue-700">Invités</div>
            <p className="text-xs text-gray-400 mt-1">Fiches police</p>
          </Link>
        </div>
      </div>

      {/* Main Content Grid: Staff + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <StaffList staff={staff} onEditStaff={handleEditStaff} />
        </div>
        <div className="lg:col-span-1">
          <RecentActivityFeed activities={recentActivity} />
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">État du Système</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Connexion API</p>
              <p className="text-xs text-gray-500">Opérationnel</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Synchronisation</p>
              <p className="text-xs text-gray-500">À jour</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Base de données</p>
              <p className="text-xs text-gray-500">Connectée</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Displays the current user's role as a badge */
function RoleBadge({ role }: { role: string }) {
  const config: Record<string, { label: string; className: string }> = {
    hotel_admin: { label: 'Administrateur — Accès complet', className: 'bg-purple-100 text-purple-800' },
    hotel_manager: { label: 'Gestionnaire — Rapports & utilisateurs', className: 'bg-blue-100 text-blue-800' },
    receptionist: { label: 'Réceptionniste — Opérations invités', className: 'bg-green-100 text-green-800' },
    staff: { label: 'Personnel', className: 'bg-gray-100 text-gray-800' },
  };
  const { label, className } = config[role] || config.staff;
  return (
    <span className={`inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full ${className}`}>
      {label}
    </span>
  );
}
